import React, { useMemo } from "react";
import {
   Chart as ChartJS,
   CategoryScale,
   LinearScale,
   BarElement,
   ArcElement,
   Tooltip,
   Legend,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
   CategoryScale,
   LinearScale,
   BarElement,
   ArcElement,
   Tooltip,
   Legend
);

const FLEET_DISTRIBUTION = [
   { label: "Tankers", value: 60, percent: 47, color: "#3b82f6" },
   { label: "Trailers", value: 35, percent: 27, color: "#22c55e" },
   { label: "Trucks", value: 20, percent: 16, color: "#f97316" },
   { label: "Others", value: 13, percent: 10, color: "#8b5cf6" },
];

const FLEET_TOTAL = FLEET_DISTRIBUTION.reduce((sum, item) => sum + item.value, 0);

const TOP_LOCATIONS = [
   { label: "Haldia", value: 22, color: "#3b82f6" },
   { label: "Tapukara", value: 18, color: "#22c55e" },
   { label: "Pune", value: 15, color: "#f97316" },
   { label: "Bankura", value: 12, color: "#8b5cf6" },
   { label: "Manesar", value: 10, color: "#14b8a6" },
];

const RECENT_ACTIVITIES = [
   {
      id: 1,
      text: "Trip WB78B2640 completed",
      time: "2 mins ago",
      tone: "green",
      icon: "fas fa-truck",
   },
   {
      id: 2,
      text: "New vehicle NL01AK1561 added",
      time: "15 mins ago",
      tone: "blue",
      icon: "fas fa-truck",
   },
   {
      id: 3,
      text: "Driver Ajay Jain assigned",
      time: "1 hour ago",
      tone: "sky",
      icon: "fas fa-users",
   },
   {
      id: 4,
      text: "Permit expired for WB98Z1234",
      time: "2 hours ago",
      tone: "red",
      icon: "fas fa-exclamation-triangle",
   },
];

const barValueLabelsPlugin = {
   id: "barValueLabels",
   afterDatasetsDraw(chart) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      if (!meta?.data?.length) return;

      ctx.save();
      ctx.font = "600 12px sans-serif";
      ctx.fillStyle = "#0f2744";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";

      meta.data.forEach((bar, index) => {
         const value = chart.data.datasets[0]?.data?.[index];
         if (value == null) return;
         ctx.fillText(String(value), bar.x, bar.y - 6);
      });
      ctx.restore();
   },
};

const doughnutOptions = {
   responsive: true,
   maintainAspectRatio: false,
   cutout: "72%",
   plugins: {
      legend: { display: false },
      tooltip: {
         backgroundColor: "#0f2744",
         padding: 10,
         cornerRadius: 8,
      },
   },
};

const barOptions = {
   responsive: true,
   maintainAspectRatio: false,
   layout: {
      padding: { top: 18 },
   },
   plugins: {
      legend: { display: false },
      tooltip: {
         backgroundColor: "#0f2744",
         padding: 10,
         cornerRadius: 8,
      },
   },
   scales: {
      x: {
         grid: { display: false },
         ticks: { color: "#7b8ba1", font: { size: 11 } },
         border: { display: false },
      },
      y: {
         min: 0,
         max: 25,
         ticks: {
            stepSize: 5,
            color: "#94a3b8",
            font: { size: 11 },
         },
         title: {
            display: true,
            text: "Trips",
            color: "#94a3b8",
            font: { size: 11, weight: "500" },
            align: "end",
         },
         grid: {
            color: "#eef2f7",
            drawBorder: false,
         },
         border: { display: false },
      },
   },
};

const DashboardAnalyticsPanels = ({ fleetDistribution, topLocations }) => {
   const dynamicFleetDistribution = useMemo(() => {
      if (fleetDistribution?.distribution) {
         const colors = ["#3b82f6", "#22c55e", "#f97316", "#8b5cf6", "#14b8a6"];
         return fleetDistribution.distribution.map((item, index) => ({
            label: item.label,
            value: item.count,
            percent: item.percentage,
            color: colors[index % colors.length]
         }));
      }
      return FLEET_DISTRIBUTION;
   }, [fleetDistribution]);

   const dynamicFleetTotal = fleetDistribution?.total_vehicles ?? FLEET_TOTAL;

   const dynamicTopLocations = useMemo(() => {
      if (topLocations?.locations) {
         const colors = ["#3b82f6", "#22c55e", "#f97316", "#8b5cf6", "#14b8a6"];
         return topLocations.locations.map((item, index) => ({
            label: item.name,
            value: item.fleet_count,
            color: colors[index % colors.length]
         }));
      }
      return TOP_LOCATIONS;
   }, [topLocations]);

   const fleetData = useMemo(
      () => ({
         labels: dynamicFleetDistribution.map((item) => item.label),
         datasets: [
            {
               data: dynamicFleetDistribution.map((item) => item.value),
               backgroundColor: dynamicFleetDistribution.map((item) => item.color),
               borderWidth: 0,
               hoverOffset: 4,
            },
         ],
      }),
      [dynamicFleetDistribution]
   );

   const locationData = useMemo(
      () => ({
         labels: dynamicTopLocations.map((item) => item.label),
         datasets: [
            {
               data: dynamicTopLocations.map((item) => item.value),
               backgroundColor: dynamicTopLocations.map((item) => item.color),
               borderRadius: 8,
               borderSkipped: false,
               barPercentage: 0.55,
               categoryPercentage: 0.7,
            },
         ],
      }),
      [dynamicTopLocations]
   );

   return (
      <div className="row oil-analytics-row">
         <div className="col-xl-4 col-lg-6 col-12">
            <div className="oil-panel oil-analytics-panel">
               <div className="oil-panel__header">
                  <h3 className="oil-panel__title">Fleet Distribution</h3>
               </div>
               <div className="oil-fleet-layout">
                  <div className="oil-fleet-donut">
                     <Doughnut options={doughnutOptions} data={fleetData} />
                     <div className="oil-fleet-donut__center">
                        <div className="oil-fleet-donut__total">{dynamicFleetTotal}</div>
                        <div className="oil-fleet-donut__caption">Total Vehicles</div>
                     </div>
                  </div>
                  <ul className="oil-status-legend">
                     {dynamicFleetDistribution.map((item) => (
                        <li key={item.label} className="oil-status-legend__item">
                           <span className="oil-status-legend__left">
                              <span
                                 className="oil-status-legend__dot"
                                 style={{ backgroundColor: item.color }}
                              />
                              <span className="oil-status-legend__label">
                                 {item.label}
                              </span>
                           </span>
                           <span className="oil-status-legend__value">
                              {item.value} ({item.percent}%)
                           </span>
                        </li>
                     ))}
                  </ul>
               </div>
            </div>
         </div>

         <div className="col-xl-8 col-lg-6 col-12">
            <div className="oil-panel oil-analytics-panel">
               <div className="oil-panel__header">
                  <h3 className="oil-panel__title">Top Locations</h3>
               </div>
               <div className="oil-locations-chart">
                  <Bar
                     options={barOptions}
                     data={locationData}
                     plugins={[barValueLabelsPlugin]}
                  />
               </div>
            </div>
         </div>

         {/* <div className="col-xl-4 col-lg-12 col-12">
            <div className="oil-panel oil-analytics-panel">
               <div className="oil-panel__header">
                  <h3 className="oil-panel__title">Recent Activities</h3>
               </div>
               <ul className="oil-activity-list">
                  {RECENT_ACTIVITIES.map((item) => (
                     <li key={item.id} className="oil-activity-item">
                        <span
                           className={`oil-activity-item__icon oil-activity-item__icon--${item.tone}`}
                        >
                           <i className={item.icon} />
                        </span>
                        <span className="oil-activity-item__text">{item.text}</span>
                        <span className="oil-activity-item__time">{item.time}</span>
                     </li>
                  ))}
               </ul>
            </div>
         </div> */}
      </div>
   );
};

export default DashboardAnalyticsPanels;
